/**
 * Look ahead — tomorrow, the rest of this week, next week: compact day cards with the count against
 * capacity ("3 of 5", with a bar) and the names.
 *
 * 1.5.17: the whole day card is one button — it opens that day as a read-only list (features/dayahead), where the
 * link to the day on the Schedule now is. The card header's "Open the schedule" stays.
 */
import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AppointmentRow, SlotRow } from '@shared/api-types';
import { addDaysToKey, isWeekdayKey, mondayOfKey } from '@shared/dates';
import { formatDayShort } from '@/lib/dates';
import { useAppointments, useSlots } from '@/lib/queries';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CapacityMeter } from '@/components/CapacityMeter';
import { Updated } from '@/components/Updated';
import { DayAheadSheet } from '@/features/dayahead/DayAheadSheet';

const COUNTED = new Set(['Scheduled', 'On Hold', 'In Progress', 'Completed', 'No Show', 'Unable to Complete']);

export function dayCounts(rows: AppointmentRow[]) {
  const booked = rows.filter((r) => COUNTED.has(r.status) && r.kind !== 'facility');
  const proposed = rows.filter((r) => r.status === 'Proposed');
  return { booked, proposed };
}

function DayCard({ dayKey, rows, slot, onOpen }: { dayKey: string; rows: AppointmentRow[]; slot: SlotRow | undefined; onOpen: (dayKey: string, el: HTMLElement) => void }) {
  const { t, i18n } = useTranslation();
  const { booked, proposed } = dayCounts(rows);
  const shown = booked.slice(0, 4);
  // one button for the whole card (a button holds phrasing content only: the names are spans, not a list)
  return (
    <li className="ahead-day">
      <button
        type="button"
        className="ahead-open"
        aria-label={`${formatDayShort(dayKey, i18n.language)} · ${slot ? t('schedule.ofCapacity', { n: booked.length, cap: slot.baseCapacity }) : t('schedule.noSlot')}`}
        aria-haspopup="dialog"
        onClick={(e) => onOpen(dayKey, e.currentTarget)}
        data-testid={`ahead-${dayKey}`}
      >
        <span className="ahead-date">{formatDayShort(dayKey, i18n.language)}</span>
        {slot ? <CapacityMeter used={booked.length} capacity={slot.baseCapacity} /> : <span className="help">{t('schedule.noSlot')}</span>}
        {shown.length ? (
          <span className="ahead-names">
            {shown.map((r) => (
              <span key={r.id}>
                <b className="tnum">{r.unit}</b> {r.tenantName}
                {r.status === 'On Hold' ? ` · ${t('status.onHold')}` : r.confirmation !== 'Confirmed' && r.kind === 'tenant' ? ` · ${t('status.awaiting')}` : ''}
              </span>
            ))}
            {booked.length > shown.length ? <span className="help">{t('schedule.andMore', { count: booked.length - shown.length })}</span> : null}
          </span>
        ) : null}
        {proposed.length ? <span className="help">{t('schedule.proposedCount', { count: proposed.length })}</span> : null}
      </button>
    </li>
  );
}

export function LookAhead({ today }: { today: string }) {
  const { t } = useTranslation();
  const tomorrow = addDaysToKey(today, 1);
  const monday = mondayOfKey(today);
  const friday = addDaysToKey(monday, 4);
  const nextMonday = addDaysToKey(monday, 7);
  const nextFriday = addDaysToKey(monday, 11);
  const appts = useAppointments(tomorrow, nextFriday);
  const slots = useSlots(tomorrow, nextFriday);
  // 1.5.17: the day that is open as a read-only list, and the card that opened it (focus goes back there)
  const [openDay, setOpenDay] = useState<string | null>(null);
  const opener = useRef<HTMLElement | null>(null);
  const onOpen = (k: string, el: HTMLElement) => {
    opener.current = el;
    setOpenDay(k);
  };

  const days = (from: string, to: string) => {
    const out: string[] = [];
    for (let k = from; k <= to; k = addDaysToKey(k, 1)) if (isWeekdayKey(k) || slots.data?.slots.some((s) => s.serviceDateKey === k)) out.push(k);
    return out;
  };
  const groups = [
    { key: 'tomorrow', label: t('today.aheadTomorrow'), days: days(tomorrow, tomorrow) },
    { key: 'week', label: t('today.aheadRestOfWeek'), days: days(addDaysToKey(tomorrow, 1), friday) },
    { key: 'next', label: t('today.aheadNextWeek'), days: days(nextMonday, nextFriday) },
  ].filter((g) => g.days.length);

  const rowsFor = (k: string) => (appts.data?.appointments ?? []).filter((a) => a.serviceDateKey === k);
  const slotFor = (k: string) => slots.data?.slots.find((s) => s.serviceDateKey === k);

  return (
    <Card aria-labelledby="h-ahead" style={{ marginTop: 18 }} data-testid="look-ahead">
      <CardHeader>
        <CardTitle id="h-ahead">{t('today.lookAhead')}</CardTitle>
        <Link className="btn soft sm" to="/schedule">
          {t('today.openSchedule')}
        </Link>
      </CardHeader>
      <CardContent>
        {appts.isLoading || slots.isLoading ? (
          <p className="help">{t('app.loading')}</p>
        ) : appts.isError ? (
          <p className="help" role="alert">
            {t('today.aheadError')}
          </p>
        ) : (
          groups.map((g) => (
            <div key={g.key} className="ahead-group">
              <h3 id={`h-ahead-${g.key}`}>{g.label}</h3>
              <ul className="ahead-days" aria-labelledby={`h-ahead-${g.key}`}>
                {g.days.map((k) => (
                  <DayCard key={k} dayKey={k} rows={rowsFor(k)} slot={slotFor(k)} onOpen={onOpen} />
                ))}
              </ul>
            </div>
          ))
        )}
        <Updated at={appts.dataUpdatedAt} />
      </CardContent>
      <DayAheadSheet day={openDay} onClose={() => setOpenDay(null)} returnFocusTo={opener} />
    </Card>
  );
}
